/** @jsxImportSource @emotion/react */
import { Button, Icon, Tooltip } from "@blueprintjs/core"
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from "react-hook-form"

import { RowCell } from "../../../../../styled/table.styled"
import { ButtonWithAlert } from "../../../../components"
import {
  DecimalInput,
  InputWrapper,
  IntegerInput,
  PercentageInput,
  TimeInput,
} from "../../../../components/rhf"
import InstrumentSuggest from "../../../Config/components/InstrumentSuggest"
import { assertOrderEquals } from "../../utils/checkEmptyWave"
import { DollarAmountInput } from "./DollarAmountInput"
import {
  BookText,
  InputContainer,
  bp5InputCss,
  inputCss,
  SideRowCell,
  sideSelectCss,
} from "../OrdersTable.styled"
import { ToggleInput } from "./ToggleInput"
import { WaveSelect } from "./WaveSelect"
import { Broker, getBrokersFromInstrumentAccount } from "../types/brokers"
import { OrderType, Side } from "../types/common"
import { ExecutionType, getExecutionTypesForBrokerAndOrderType } from "../types/execution"
import { OrderInputs, WaveInputs } from "../types/form"
import { useEffect, useMemo, useState } from "react"
import { ScientTradingAccount } from "../../types"

interface OrdersTableRowProps {
  index: number
  accounts: ScientTradingAccount[]
  control: Control<FieldValues & WaveInputs>
  register: UseFormRegister<FieldValues & WaveInputs>
  deleteOrder: (index: number) => void
  copyPreviousOrder: (index: number) => void
  errors: FieldErrors<WaveInputs>
  watch: UseFormWatch<WaveInputs>
  directOrdersEnabled: boolean
  setValue: UseFormSetValue<WaveInputs>
  defaultOrder: OrderInputs
}

const OrdersTableRow = ({
  accounts,
  index,
  control,
  register,
  deleteOrder,
  copyPreviousOrder,
  errors,
  watch,
  directOrdersEnabled,
  setValue,
  defaultOrder,
}: OrdersTableRowProps) => {
  const rowState = useWatch({ name: `orders[${index}]`, control })
  const watchInstrument = useWatch({ name: `orders[${index}].instrument`, control })
  const watchAmountUsd = useWatch({ name: `orders[${index}].amount_usd`, control })
  const watchQuantity = useWatch({ name: `orders[${index}].quantity`, control })
  const watchOrderType = useWatch({ name: `orders[${index}].order_type`, control })
  const watchBroker = useWatch({ name: `orders[${index}].broker`, control })
  const watchAccountId = useWatch({ name: `account_id`, control })

  /**
   * Memoized options for broker
   */
  const brokerOptions = useMemo<Record<string, string>>(() => {
    const accountName = accounts.find(account => account.id === Number(watchAccountId))?.name || ""
    return getBrokersFromInstrumentAccount(watchInstrument, directOrdersEnabled, accountName)
  }, [watchInstrument, directOrdersEnabled, accounts, watchAccountId])

  /**
   * Memoized options for execution type
   */
  const executionTypeOptions = useMemo<Record<string, string>>(
    () => getExecutionTypesForBrokerAndOrderType(watchBroker, watchOrderType),
    [watchBroker, watchOrderType],
  )

  const [lastAmountFieldUpdated, setLastAmountFieldUpdated] = useState<
    "amount_usd" | "quantity" | null
  >(null)

  const isDefaultState = assertOrderEquals(rowState, defaultOrder)

  /**
   * Reset local state when form is reset to default state
   */
  useEffect(() => {
    if (isDefaultState) {
      setLastAmountFieldUpdated(null)
    }
  }, [isDefaultState])

  /**
   * Effect to compute quantity from amount_usd and vice versa based on which field was last updated
   */
  useEffect(() => {
    const amountUsd = watchAmountUsd
    const quantity = watchQuantity
    const instrument = watchInstrument
    const latestPriceUsd = instrument?.latest_price_usd

    if (!latestPriceUsd) return

    if (lastAmountFieldUpdated === "amount_usd") {
      // Handle clearing amount_usd field
      if (!amountUsd) {
        setValue(`orders[${index}].quantity`, null)
        return
      }

      // Update quantity if amount_usd was changed
      if (amountUsd && latestPriceUsd) {
        const calculatedQuantity = Math.ceil(amountUsd / latestPriceUsd)
        if (calculatedQuantity > 0) {
          setValue(`orders[${index}].quantity`, calculatedQuantity)
        }
      }
    } else if (lastAmountFieldUpdated === "quantity") {
      // Handle clearing quantity field
      if (!quantity) {
        setValue(`orders[${index}].amount_usd`, null)
        return
      }

      // Update amount_usd if quantity was changed
      if (quantity && latestPriceUsd) {
        const calculatedAmountUsd = Number((quantity * latestPriceUsd).toFixed(2))
        if (calculatedAmountUsd > 0) {
          setValue(`orders[${index}].amount_usd`, calculatedAmountUsd)
        }
      }
    }
  }, [watchAmountUsd, watchQuantity, watchInstrument, lastAmountFieldUpdated, setValue, index])

  /**
   * Effect to set limit to latest price if order type is limit
   */
  useEffect(() => {
    if (watchOrderType === "limit") {
      setValue(`orders[${index}].limit_price`, watchInstrument?.latest_price)
    } else {
      setValue(`orders[${index}].limit_price`, undefined)
    }
  }, [watchOrderType, setValue, index, watchInstrument])

  return (
    <>
      <RowCell index={index}>
        {index !== 0 && (
          <Tooltip content="Copy previous order" placement="bottom" hoverOpenDelay={1500}>
            <Button icon="bring-data" onClick={() => copyPreviousOrder(index)} />
          </Tooltip>
        )}
      </RowCell>
      <RowCell index={index}>
        <InstrumentSuggest
          name={`orders[${index}].instrument`}
          control={control}
          rules={{
            required: !isDefaultState && "Ticker is required",
            deps: [`orders[${index}].broker`],
          }}
          customCss={bp5InputCss(index)}
          withPriceUsd={true}
          onChange={() => {
            setValue(`orders[${index}].amount_usd`, null)
            setValue(`orders[${index}].quantity`, null)
          }}
        />
      </RowCell>
      <SideRowCell index={index} side={watch(`orders[${index}].side`)}>
        <WaveSelect
          name={`orders[${index}].side` as const}
          optionsEnum={Side}
          control={control}
          rules={{ required: !isDefaultState && "Side is required" }}
          customCss={sideSelectCss(watch(`orders[${index}].side`))}
        />
      </SideRowCell>
      <RowCell index={index}>
        <DollarAmountInput
          index={index}
          name={`orders[${index}].amount_usd` as const}
          control={control}
          rules={{
            max: {
              value: 5000000,
              message: "USD is limited to 5M",
            },
            validate: (value: number) => {
              if (!isDefaultState && !value) {
                const quantityValue = watch(`orders[${index}].quantity`)
                if (!quantityValue) {
                  return "Either USD or quantity is required"
                }
              }
              // Validation passed
              return true
            },
            deps: [`orders[${index}].quantity`],
          }}
          latestPriceUsd={watch(`orders[${index}].instrument.latest_price_usd`)}
          latestPrice={watch(`orders[${index}].instrument.latest_price`)}
          latestPriceLatency={watch(`orders[${index}].instrument.latest_price_latency`)}
          onChange={() => setLastAmountFieldUpdated("amount_usd")}
        />
      </RowCell>
      <RowCell index={index}>
        <InputWrapper error={errors?.orders && errors.orders[index]?.quantity}>
          <IntegerInput
            registerReturn={register(`orders[${index}].quantity`, {
              validate: (value: number) => {
                if (!isDefaultState && !value) {
                  const amountUsdValue = watch(`orders[${index}].amount_usd`)
                  if (!amountUsdValue) {
                    return "Either quantity or USD is required"
                  }
                  if (amountUsdValue) {
                    return "Unable to compute quantity from amount_usd, please enter a quantity directly"
                  }
                }
                return true
              },
              valueAsNumber: true,
              deps: [`orders[${index}].amount_usd`],
            })}
            min={1}
            onChange={() => setLastAmountFieldUpdated("quantity")}
          />
        </InputWrapper>
      </RowCell>
      <RowCell index={index}>
        <WaveSelect
          name={`orders[${index}].order_type`}
          optionsEnum={OrderType}
          control={control}
          rules={{
            required: !isDefaultState ? "Type is required" : false,
            deps: [`orders[${index}].limit_price`],
          }}
        />
      </RowCell>
      <RowCell index={index}>
        <InputWrapper error={errors?.orders && errors.orders[index]?.limit_price}>
          <DecimalInput
            name={`orders[${index}].limit_price`}
            control={control}
            rules={{
              validate: (value: string) => {
                const orderTypeValue = watch(`orders[${index}].order_type`)
                if (!isDefaultState) {
                  if (orderTypeValue === "limit" && !value) {
                    return "Limit is required when order Type is 'Limit'"
                  }
                  if (value && orderTypeValue === "market") {
                    return "Limit should be empty if Type is 'Market'"
                  }
                }
                return true
              },
            }}
            min={0.01}
          />
        </InputWrapper>
      </RowCell>
      <RowCell index={index}>
        <WaveSelect
          name={`orders[${index}].broker`}
          control={control}
          optionsEnum={brokerOptions}
          disabled={!directOrdersEnabled}
        />
      </RowCell>
      <RowCell index={index}>
        <WaveSelect
          name={`orders[${index}].execution_type`}
          optionsEnum={executionTypeOptions}
          control={control}
          rules={{
            validate: value => {
              if (!isDefaultState) {
                const instrument = watch(`orders[${index}].instrument`)
                const bloombergCode = instrument?.bloomberg_code
                const exchangeCountryCode = instrument?.exchange_country_code

                // Only proceed with market checks if bloomberg_code exists
                if (bloombergCode) {
                  if (bloombergCode.endsWith("INDEX") && value !== ExecutionType.FUTURE) {
                    return "Execution type must be FUTURE when ticker ends by INDEX"
                  }
                }

                if (exchangeCountryCode) {
                  if (
                    !["US", "GF"].includes(exchangeCountryCode) &&
                    value === ExecutionType.PRE_MARKET
                  ) {
                    return "Pre market works only for US or GF market"
                  }
                }
              }
              return true
            },
            deps: [
              `orders[${index}].time_in_market`,
              `orders[${index}].execution_details`,
              `orders[${index}].instrument`,
            ],
          }}
        />
      </RowCell>
      <RowCell index={index}>
        <ToggleInput
          name={`orders[${index}].auction` as const}
          index={index}
          control={control}
          disabled={
            !directOrdersEnabled ||
            ![Broker.JPM, Broker.GS].includes(watch(`orders[${index}].broker`))
          }
        />
      </RowCell>
      <RowCell index={index}>
        <InputWrapper error={errors?.orders && errors.orders[index]?.execution_cap_pct}>
          <PercentageInput
            registerReturn={register(`orders[${index}].execution_cap_pct`, {
              setValueAs: (value: number) => {
                return isNaN(value) ? "" : (value / 100).toString()
              },
            })}
          />
        </InputWrapper>
      </RowCell>
      <RowCell index={index}>
        <InputWrapper error={errors?.orders && errors.orders[index]?.time_in_market}>
          <IntegerInput
            registerReturn={register(`orders[${index}].time_in_market` as const, {
              validate: (value: number) => {
                const executionTypeValue = watch(`orders[${index}].execution_type`)
                const targetEndValue = watch(`orders[${index}].target_end_datetime`)
                if (!isDefaultState) {
                  if (value && executionTypeValue === "here") {
                    return "Time in Market should not be specified if execution type is 'Here'"
                  }

                  if (value && targetEndValue) {
                    return "Time in Market should be empty if Target End is filled"
                  }
                }
                return true
              },
              valueAsNumber: true,
              deps: [`orders[${index}].execution_type`, `orders[${index}].target_end_datetime`],
            })}
            min={5}
            max={480}
          />
        </InputWrapper>
      </RowCell>
      <RowCell index={index}>
        <InputWrapper error={errors?.orders && errors.orders[index]?.target_end_datetime}>
          <TimeInput
            registerReturn={register(`orders[${index}].target_end_datetime`, {
              deps: [`orders[${index}].time_in_market`],
            })}
          />
        </InputWrapper>
      </RowCell>
      <RowCell index={index}>
        <InputContainer>
          <input
            type="text"
            {...register(`orders[${index}].execution_details` as const, {
              validate: (value: string) => {
                const executionTypeValue = watch(`orders[${index}].execution_type`)
                const brokerValue = watch(`orders[${index}].broker`)
                if (!isDefaultState) {
                  if (executionTypeValue === "pair" && !value) {
                    return "Execution detail is required when execution type is 'Pair'"
                  }
                  if (brokerValue === Broker.EXEC_DESK_PM && !value) {
                    return "Execution detail is required when broker is 'EXEC DESK - PM'"
                  }
                  return true
                }
                return true
              },
            })}
            css={inputCss(index)}
          />
          {errors?.orders && errors.orders[index]?.execution_details && (
            <Tooltip
              content={errors?.orders[index]?.execution_details?.message}
              placement="bottom"
              openOnTargetFocus={false}
              compact
            >
              <Icon icon="error" size={20} intent="warning" />
            </Tooltip>
          )}
        </InputContainer>
      </RowCell>

      <RowCell index={index}>
        <ButtonWithAlert
          buttonProps={{
            icon: "trash",
          }}
          alertProps={{
            intent: "danger",
            confirmButtonText: "Yes, delete",
            onConfirm: () => deleteOrder(index),
          }}
        >
          <p>Do you really want to delete this order?</p>
        </ButtonWithAlert>
      </RowCell>
    </>
  )
}

export default OrdersTableRow
